import { getActiveChatContext, setActiveChatContext } from '../../../../utils/activeChatContext';
import { getSocket } from '../../../../lib/socket';
import { SOCKET_EVENTS } from '../../../../lib/socketEvents';
import { useCallback, useEffect, useRef, useState } from 'react';
import { resolveMessageTopic, listChatTopics, markChatTopicRead, renameChatTopic, setChatTopicVisibility } from '../../../../api/chatTopics';

export function topicOfMessage(message: any) {
  return message?.topic || (message?.topic_id ? { id: message.topic_id, name: message.topic_name || 'スレッド' } : null);
}
export function newestMessageId(messages: any[]) {
  return [...(messages || [])].filter((row: any) => row?.id).sort((a, b) =>
    String(b.created_at || '').localeCompare(String(a.created_at || '')) || String(b.id).localeCompare(String(a.id)))[0]?.id;
}

export function rememberNewestMessage(controller: any, messages: any[]) {
  const latest = [...(messages || [])].filter((row: any) => row?.id).sort((a, b) =>
    String(b.created_at || '').localeCompare(String(a.created_at || '')) || String(b.id).localeCompare(String(a.id)))[0];
  if (!latest) return;
  const cursor = `${latest.created_at || ''}:${latest.id}`;
  if (controller.parentMessageKey !== controller.key || !controller.parentMessageCursor || cursor >= controller.parentMessageCursor) {
    controller.parentMessageKey = controller.key;
    controller.parentMessageCursor = cursor;
    controller.parentLastMessage = latest;
  }
  if (controller.lastMessageMode !== controller.modeKey || !controller.lastMessageCursor || cursor >= controller.lastMessageCursor) {
    controller.lastMessageMode = controller.modeKey;
    controller.lastMessageCursor = cursor;
    controller.lastMessageId = latest.id;
  }
}

export function isTopicRequestSuperseded(error: any) {
  return error?.code === 'CHAT_TOPIC_REQUEST_SUPERSEDED';
}
function assertCurrentTopicRequest(controller: any, modeKey: string) {
  if (controller.modeKey === modeKey) return;
  throw Object.assign(new Error('表示するスレッドが切り替わりました。'), { name: 'AbortError', code: 'CHAT_TOPIC_REQUEST_SUPERSEDED' });
}
function captureTopicRequest(controller: any, conversationId?: string) {
  // Selection handlers can request the new parent before React commits its state.
  // In that case the previous parent's topic/draft must never scope the request.
  if (controller.parentKey && conversationId && String(conversationId) !== controller.conversationId) {
    const selected = controller.pendingSelection?.conversationId === String(conversationId) ? controller.pendingSelection.topic : null;
    return { modeKey: `${controller.parentKey}:${conversationId}:${selected?.id || selected?.clientId || 'all'}`, topicId: selected?.id, draftTopic: selected?.clientId && !selected?.id };
  }
  return { modeKey: controller.modeKey, membershipRevision: controller.membershipRevision || 0, topicId: controller.topicId, draftTopic: controller.draftTopic };
}

// Requests keep their intended parent/topic across React selection and network awaits.
export function bindTopicMessageApi(controller: any, request: (...args: any[]) => Promise<any>, optionsIndex: number, conversationIdIndex: number | null = 0) {
  return async (...args: any[]) => {
    const snapshot = captureTopicRequest(controller, conversationIdIndex === null ? undefined : args[conversationIdIndex]);
    while (args.length <= optionsIndex) args.push(undefined);
    if (snapshot.draftTopic) return { messages: [], hasMore: false };
    args[optionsIndex] = { ...(args[optionsIndex] || {}), ...(snapshot.topicId ? { topicId: snapshot.topicId } : {}) };
    const result = await request(...args);
    assertCurrentTopicRequest(controller, snapshot.modeKey);
    const messages = Array.isArray(result) ? result : result?.messages;
    if (Array.isArray(messages)) {
      controller.loadedMembershipRevision = messages.length ? Math.min(...messages.map((m: any) => Number(m.conversation_revision || 0))) : snapshot.membershipRevision || 0;
      controller.loadedMessageModeKey = snapshot.modeKey;
      rememberNewestMessage(controller, messages);
    }
    return result;
  };
}

export default function useChatTopics({ scope, conversationId, role, accountId, enabled = true }: { scope: "individual" | "event" | "group"; conversationId?: string; role: "admin" | "artist"; accountId?: string | number; enabled?: boolean }) {
  const key = `${role}:${accountId || ''}:${scope}:${conversationId || ''}`;
  const [state, setState] = useState<any>({ key: '', rows: [], selected: null, loaded: false, invitation: false, open: false, error: '', nextCursor: null, revision: 0 });
  const valueRef = useRef<any>({});
  const value = valueRef.current;
  const current = state.key === key ? state : { key, rows: [], selected: value.pendingSelection?.conversationId === String(conversationId) ? value.pendingSelection.topic : null, loaded: false, invitation: false, open: false, error: '', nextCursor: null, revision: 0 };
  const parent = { actorType: role, scope, conversationId: String(conversationId || '') };
  const previousModeKey = value.modeKey;
  Object.assign(value, current, parent, { role, parentKey: `${role}:${accountId || ''}:${scope}`, enabled: Boolean(enabled && conversationId), topicId: current.selected?.id || '', draftTopic: current.selected?.clientId && !current.selected?.id ? current.selected : null, ready: current.loaded && !current.invitation, key, modeKey: `${key}:${current.selected?.id || current.selected?.clientId || 'all'}` });
  if (previousModeKey !== value.modeKey) value.loadedMessageModeKey = '';

  const refresh = useCallback(async ({ initial = false, append = false } = {}) => {
    const expected = key;
    if (!enabled || !conversationId) return;
    try {
      const result = await listChatTopics({ actorType: role, scope, conversationId: String(conversationId), includeHidden: true, limit: 20, ...(append && value.nextCursor ? { cursor: value.nextCursor } : {}) });
      if (value.key !== expected) return;
      const url = new URLSearchParams(window.location.search);
      const messageId = initial ? url.get('message') || url.get('messageId') || url.get('message_id') : '';
      const target = messageId ? await resolveMessageTopic({ actorType: role, scope, conversationId: String(conversationId), messageId }) : null;
      if (value.key !== expected) return;
      if (messageId) value.pendingAnchorMessageId = messageId;
      const rows = result.topics || [];
      setState((previous: any) => {
        const same = previous.key === expected;
        const directId = initial ? (new URLSearchParams(window.location.search).get('topicId') || new URLSearchParams(window.location.search).get('topic')) : '';
        const selected = target ? target.topic || null : value.pendingSelection?.conversationId === String(conversationId) ? value.pendingSelection.topic : directId ? rows.find((row: any) => row.id === directId) || { id: directId, name: 'スレッド' } : same ? rows.find((row: any) => row.id === previous.selected?.id) || previous.selected : null;
        return { ...(same ? previous : {}), key: expected, membershipRevision: result.membershipRevision || 0, rows: append && same ? [...previous.rows, ...rows.filter((row: any) => !previous.rows.some((item: any) => item.id === row.id))] : rows, selected, loaded: true, invitation: initial && !selected && rows.some((row: any) => row.unreadCount > 0), open: same ? previous.open : false, error: '', nextCursor: result.nextCursor };
      });
    } catch (error: any) {
      if (value.key === expected) setState((previous: any) => ({ ...previous, key: expected, error: error.message, loaded: previous.key === expected && previous.loaded }));
    }
  }, [conversationId, enabled, key, role, scope, value]);
  useEffect(() => { value.lastMessageId = ''; refresh({ initial: true }); }, [key, refresh, value]);

  useEffect(() => {
    if (!enabled || !conversationId) return undefined;
    const socket = getSocket();
    const eventName = scope === 'event' ? SOCKET_EVENTS.NEW_EVENT_MESSAGE : scope === 'group' ? SOCKET_EVENTS.NEW_GROUP_CHAT_MESSAGE : SOCKET_EVENTS.NEW_MESSAGE;
    let timer: ReturnType<typeof setTimeout>;
    const refreshForMessage = (payload: any = {}) => {
      const target = scope === 'event' ? payload.eventId || payload.event_uuid : scope === 'group' ? payload.threadId || payload.thread_id : payload.artistId || payload.artist_id;
      if (target && String(target) !== String(conversationId)) return;
      clearTimeout(timer);
      timer = setTimeout(() => { if (document.visibilityState !== 'hidden') refresh(); }, 300);
    };
    socket.on(eventName, refreshForMessage);
    return () => { clearTimeout(timer); socket.off(eventName, refreshForMessage); };
  }, [conversationId, enabled, refresh, scope]);

  useEffect(() => {
    if (!enabled || !conversationId) return undefined;
    const socket = getSocket();
    const onTopicsUpdated = (payload: any = {}) => {
      if (payload.scope !== scope || String(payload.conversationId) !== String(conversationId)) return;
      refresh();
      setState((previous: any) => ({ ...previous, revision: (previous.revision || 0) + 1 }));
    };
    socket.on(SOCKET_EVENTS.CHAT_TOPICS_UPDATED, onTopicsUpdated);
    return () => socket.off(SOCKET_EVENTS.CHAT_TOPICS_UPDATED, onTopicsUpdated);
  }, [conversationId, enabled, refresh, scope]);

  useEffect(() => {
    const active = getActiveChatContext();
    const activeId = scope === 'event' ? active?.eventId : scope === 'group' ? active?.threadId : active?.artistId;
    if (active && active.scope === role && (String(activeId) === String(conversationId) || (scope === 'individual' && role === 'artist'))) {
      setActiveChatContext({ ...active, topicId: value.topicId || value.draftTopic?.clientId || '' });
    }
  }, [conversationId, role, scope, value, value.modeKey]);

  value.canClearParentUnread = () => value.ready && !value.selected && value.parentUnreadCount === 0 && Boolean(value.lastMessageId) && value.readKey === `${value.modeKey}:${value.loadedMembershipRevision || 0}:${value.lastMessageId}`;
  value.refresh = refresh;
  value.invalidateMessages = () => setState((previous: any) => ({ ...previous, revision: (previous.revision || 0) + 1 }));
  value.selectTopic = (topic: any) => {
    value.pendingSelection = null;
    value.lastMessageId = '';
    setState((previous: any) => ({ ...previous, key, selected: topic, invitation: false, open: false }));
  };
  value.selectTargetTopic = (targetId: string, topic: any) => {
    value.pendingSelection = { conversationId: String(targetId), topic };
    if (String(targetId) === value.conversationId) value.selectTopic(topic);
  };
  value.openMessage = async (messageId: string) => {
    const expected = value.key;
    const result = await resolveMessageTopic({ ...parent, messageId });
    if (value.key !== expected) return;
    value.pendingAnchorMessageId = messageId;
    value.selectTopic(result.topic || null);
    value.invalidateMessages();
  };
  value.openPicker = () => setState((previous: any) => ({ ...previous, key, open: true }));
  value.closePicker = () => setState((previous: any) => ({ ...previous, open: false }));
  value.dismissInvitation = () => setState((previous: any) => ({ ...previous, invitation: false }));
  value.acceptInvitation = () => {
    const unread = value.rows.filter((row: any) => row.unreadCount > 0);
    if (unread.length === 1) value.selectTopic(unread[0]);
    else setState((previous: any) => ({ ...previous, open: true }));
  };
  value.sendFields = { topicAware: true, ...(current.selected?.id ? { topicId: current.selected.id } : current.selected?.clientId ? { newTopic: { clientId: current.selected.clientId, kind: current.selected.kind, ...(current.selected.kind === 'event' ? { eventId: current.selected.eventId } : { name: current.selected.name }) } } : {}) };
  value.resolveSendFields = (fields: any) => fields.newTopic && value.promotedTopics?.[fields.newTopic.clientId]
    ? { topicId: value.promotedTopics[fields.newTopic.clientId] } : fields;
  value.onSent = async (result: any, sentModeKey?: string, sentFields?: any) => {
    const created = result?.topic || result?.message?.topic;
    if (created?.id && sentFields?.newTopic) value.promotedTopics = { ...value.promotedTopics, [sentFields.newTopic.clientId]: created.id };
    if (sentModeKey && sentModeKey !== value.modeKey) return;
    if (created?.id && value.draftTopic) value.selectTopic(created);
    await refresh();
  };
  value.markRead = async (messageId = value.lastMessageId) => {
    if (!value.ready || value.selectionActive || value.draftTopic || !messageId || document.visibilityState === 'hidden') return false;
    const modeKey = value.modeKey;
    const dedupKey = `${modeKey}:${value.loadedMembershipRevision || 0}:${messageId}`;
    if (value.readKey === dedupKey) return true;
    const result = await markChatTopicRead({ actorType: value.role, scope: value.scope, conversationId: value.conversationId, ...(value.topicId ? { topicId: value.topicId } : {}), readScope: value.topicId ? 'topic' : 'all', lastReadMessageId: messageId, membershipRevision: value.loadedMembershipRevision || 0 });
    if (modeKey !== value.modeKey) return false;
    value.readKey = dedupKey;
    value.parentUnreadCount = result.unreadCount;
    if (Number.isFinite(result.unreadCount)) value.onParentUnread?.(result.unreadCount);
    setState((previous: any) => ({ ...previous, rows: previous.rows.map((row: any) => !value.topicId || row.id === value.topicId ? { ...row, unreadCount: value.topicId ? result.topicUnreadCount ?? row.unreadCount : result.unreadCount === 0 ? 0 : row.unreadCount } : row) }));
    if (!value.topicId && result.unreadCount > 0) void refresh();
    window.dispatchEvent(new CustomEvent('chat-topics-read', { detail: { scope: value.scope, role: value.role, conversationId: value.conversationId, unreadCount: result.unreadCount } }));
    return true;
  };
  value.rename = async (topic: any, name: string) => { await renameChatTopic(topic.id, { ...parent, name, revision: topic.revision }); await refresh(); };
  value.hide = async (topic: any, hidden: boolean) => { await setChatTopicVisibility(topic.id, { ...parent, hidden }); await refresh(); };
  return value;
}

export function useTopicMessageState(controller: any): [any[], (change: any) => void] {
  const [messages, setMessages] = useState<any[]>([]);
  const update = useCallback((change: any) => {
    setMessages((previous: any) => {
      const next = typeof change === 'function' ? change(previous) : change;
      if (controller.draftTopic) return [];
      return controller.topicId ? next.filter((message: any) => String(topicOfMessage(message)?.id || '') === String(controller.topicId)) : next;
    });
  }, [controller]);
  return [messages, update];
}

export function useTopicModeReload(controller: any, reload: (anchorMessageId?: string) => any, clear?: () => void) {
  const current = useRef({ reload, clear });
  current.current = { reload, clear };
  useEffect(() => {
    if (!controller.enabled || !controller.ready) return;
    current.current.clear?.();
    const anchor = controller.pendingAnchorMessageId || '';
    controller.pendingAnchorMessageId = '';
    if (!controller.draftTopic) Promise.resolve(current.current.reload?.(anchor)).catch(() => {});
  }, [controller, controller.modeKey, controller.ready, controller.revision, controller.membershipRevision]);
}

export function bindTopicUploadApi(controller: any, request: (...args: any[]) => Promise<any>) {
  return async (...args: any[]) => {
    const modeKey = controller.modeKey;
    const index = args.length - 1;
    const source = args[index];
    if (source instanceof FormData) {
      const body = new FormData();
      source.forEach((item, name) => body.append(name, item));
      body.set('topicAware', 'true');
      if (controller.sendFields.topicId) body.set('topicId', controller.sendFields.topicId);
      if (controller.sendFields.newTopic) body.set('newTopic', JSON.stringify(controller.sendFields.newTopic));
      args[index] = body;
    } else {
      args[index] = { ...source, ...controller.sendFields };
    }
    const result = await request(...args);
    await controller.onSent(result, modeKey);
    return result;
  };
}

export function bindTopicUrlReadApi(controller: any, request: (...args: any[]) => Promise<any>) {
  return async (inputUrl: string, ...args: any[]) => {
    if (!/\/event-chat\/events\/[^/]+\/messages\//.test(inputUrl)) return request(inputUrl, ...args);
    const url = new URL(inputUrl, window.location.origin);
    const conversationId = decodeURIComponent(url.pathname.match(/\/event-chat\/events\/([^/]+)\/messages\//)?.[1] || '');
    const snapshot = captureTopicRequest(controller, conversationId);
    if (snapshot.draftTopic) return { messages: [], hasMore: false };
    if (snapshot.topicId) url.searchParams.set('topicId', snapshot.topicId);
    const result = await request(url.toString(), ...args);
    assertCurrentTopicRequest(controller, snapshot.modeKey);
    if (Array.isArray(result?.messages)) { controller.loadedMembershipRevision = result.messages.length ? Math.min(...result.messages.map((m: any) => Number(m.conversation_revision || 0))) : snapshot.membershipRevision || 0; controller.loadedMessageModeKey = snapshot.modeKey; }
    rememberNewestMessage(controller, result?.messages);
    return result;
  };
}

// Shared receipt coordination prevents overlapping viewport/socket reads.
export function useTopicReadReceipt(controller: any, accountId: string | number, onRead?: () => void) {
  const pending = useRef(new Map());
  const completed = useRef(new Set());
  return useCallback(async (conversationId: string, messageId: string) => {
    if (!accountId || !messageId || String(conversationId) !== controller.conversationId) return false;
    const key = `${controller.modeKey}:${controller.loadedMembershipRevision || 0}:${messageId}`;
    if (completed.current.has(key)) return true;
    if (pending.current.has(key)) return pending.current.get(key);
    const request = (async () => {
      try {
        const marked = await controller.markRead(messageId);
        if (!marked) return false;
        completed.current.add(key);
        if (completed.current.size > 500) completed.current.delete(completed.current.values().next().value);
        onRead?.();
        return true;
      } catch { return false; }
      finally { pending.current.delete(key); }
    })();
    pending.current.set(key, request);
    return request;
  }, [accountId, controller, onRead]);
}

export function selectTopicForReply(controller: any, message: any, draft: any) {
  const topic = topicOfMessage(message);
  if (String(topic?.id || '') !== controller.topicId) {
    controller.pendingReplyDraft = draft;
    controller.selectTopic(topic);
  }
}

export function useTopicMessageApis(controller: any, pageRequest: (...args: any[]) => Promise<any>, aroundRequest: (...args: any[]) => Promise<any>, topicSelection: any, conversationId: string) {
  const listMessages = useCallback(bindTopicMessageApi(controller, pageRequest, 3), [controller, pageRequest]);
  const listAround = useCallback(bindTopicMessageApi(controller, aroundRequest, 4), [controller, aroundRequest]);
  const markRead = useCallback((body: any) => controller.markRead(body.lastReadMessageId), [controller]);
  useEffect(() => { if (topicSelection !== undefined) controller.selectTopic(topicSelection); }, [controller, topicSelection, conversationId]);
  return { listMessages, listAround, markRead };
}

export function useTopicReplyNavigation(controller: any, showError: (message: string) => void) {
  return useCallback(async (reply: any) => {
    const messageId = String(reply?.messageId || '').trim();
    if (!messageId || !controller.enabled) return;
    if (reply?.isUnavailable) { showError('返信元メッセージが見つかりません。'); return; }
    try { await controller.openMessage(messageId); }
    catch (error: any) { showError(error.message || '返信元メッセージを開けませんでした。'); }
  }, [controller, showError]);
}
